import { zodResolver } from '@hookform/resolvers/zod';
import CircularProgress from '@mui/material/CircularProgress';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import type { Client, ClientStatus, CreateClientDto, UpdateClientDto } from 'api';
import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { statusMenuItems, StatusOption } from 'shared/components/StatusSelect';
import { z } from 'zod';

import {
  FORM_FIELD_SX,
  FORM_MENU_ITEM_SX,
  FORM_SELECT_MENU_PROPS,
  FormDialogActions,
  FormDialogContent,
  FormFieldStack,
  ModalCancelButton,
  ModalDialog,
  ModalSubmitButton,
  ModalTitle,
} from '../clients.styles';

const CURRENCIES = ['USD', 'EUR', 'GBP', 'INR', 'AUD', 'CAD', 'SGD', 'AED'];

const clientSchema = z.object({
  name: z.string().min(1, 'Name is required').max(200),
  email: z.string().email('Invalid email').or(z.literal('')).optional(),
  address: z.string().max(500).optional(),
  note: z.string().max(2000).optional(),
  currency: z.string().optional(),
  status: z.enum(['Active', 'Inactive', 'OnHold']),
});

type ClientFormValues = z.infer<typeof clientSchema>;

interface ClientFormModalProps {
  open: boolean;
  /** Provide to open in edit mode; omit for create mode */
  client?: Client | null;
  /** Pre-fill the name field (used when opening from the inline add input) */
  initialName?: string;
  onClose: () => void;
  onSubmit: (data: CreateClientDto | UpdateClientDto) => void;
  isPending?: boolean;
}

export const ClientFormModal: React.FC<ClientFormModalProps> = ({
  open,
  client,
  initialName = '',
  onClose,
  onSubmit,
  isPending = false,
}) => {
  const isEdit = Boolean(client);

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<ClientFormValues>({
    resolver: zodResolver(clientSchema),
    defaultValues: {
      name: '',
      email: '',
      address: '',
      note: '',
      currency: '',
      status: 'Active' as ClientStatus,
    },
  });

  // Populate form when modal opens
  useEffect(() => {
    if (!open) return;
    if (client) {
      reset({
        name: client.name,
        email: client.email ?? '',
        address: client.address ?? '',
        note: client.note ?? '',
        currency: client.currency ?? '',
        status: client.status,
      });
    } else {
      reset({
        name: initialName,
        email: '',
        address: '',
        note: '',
        currency: '',
        status: 'Active',
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleFormSubmit = (values: ClientFormValues) => {
    const payload: CreateClientDto | UpdateClientDto = {
      name: values.name,
      email: values.email || undefined,
      address: values.address || undefined,
      note: values.note || undefined,
      currency: values.currency || undefined,
      ...(isEdit && { status: values.status }),
    };
    onSubmit(payload);
  };

  return (
    <ModalDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <ModalTitle>{isEdit ? 'Edit Client' : 'Add Client'}</ModalTitle>

      <form onSubmit={handleSubmit(handleFormSubmit)} noValidate>
        <FormDialogContent>
          <FormFieldStack spacing={2}>
            <TextField
              label="Name"
              required
              autoFocus
              fullWidth
              size="small"
              error={Boolean(errors.name)}
              helperText={errors.name?.message}
              sx={FORM_FIELD_SX}
              {...register('name')}
            />
            <TextField
              label="Email"
              type="email"
              fullWidth
              size="small"
              error={Boolean(errors.email)}
              helperText={errors.email?.message}
              sx={FORM_FIELD_SX}
              {...register('email')}
            />
            <TextField
              label="Address"
              fullWidth
              size="small"
              multiline
              rows={2}
              error={Boolean(errors.address)}
              helperText={errors.address?.message}
              sx={FORM_FIELD_SX}
              {...register('address')}
            />
            <Controller
              name="currency"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Currency"
                  select
                  fullWidth
                  size="small"
                  error={Boolean(errors.currency)}
                  helperText={errors.currency?.message}
                  sx={FORM_FIELD_SX}
                  slotProps={{
                    select: { MenuProps: FORM_SELECT_MENU_PROPS },
                  }}
                >
                  <MenuItem value="" sx={FORM_MENU_ITEM_SX}>
                    — None —
                  </MenuItem>
                  {CURRENCIES.map((c) => (
                    <MenuItem key={c} value={c} sx={FORM_MENU_ITEM_SX}>
                      {c}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
            {isEdit && (
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <TextField
                    label="Status"
                    select
                    fullWidth
                    size="small"
                    sx={FORM_FIELD_SX}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    inputRef={field.ref}
                    slotProps={{
                      select: {
                        MenuProps: FORM_SELECT_MENU_PROPS,
                        renderValue: (val) => <StatusOption value={val as string} />,
                      },
                    }}
                  >
                    {statusMenuItems(FORM_MENU_ITEM_SX)}
                  </TextField>
                )}
              />
            )}
            <TextField
              label="Note"
              fullWidth
              size="small"
              multiline
              rows={3}
              error={Boolean(errors.note)}
              helperText={errors.note?.message}
              sx={FORM_FIELD_SX}
              {...register('note')}
            />
          </FormFieldStack>
        </FormDialogContent>

        <FormDialogActions>
          <ModalCancelButton onClick={onClose} disabled={isPending}>
            Cancel
          </ModalCancelButton>
          <ModalSubmitButton
            type="submit"
            variant="contained"
            color="primary"
            disableElevation
            disabled={isPending}
            startIcon={isPending ? <CircularProgress size={14} color="inherit" /> : null}
          >
            {isEdit ? 'Save Changes' : 'Add Client'}
          </ModalSubmitButton>
        </FormDialogActions>
      </form>
    </ModalDialog>
  );
};
